import React, { useMemo, useState, useId } from "react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import {
  getHtmlContentStyles,
  HTML_CONTENT_CLASS,
} from "../../utils/htmlContentStyles";
import { useCardPreview } from "../../contexts/CardPreviewContext";
import { sanitizeHtml } from "../../utils/sanitizeHtml";

interface ExpandableDescriptionProps {
  content: string;
  maxLength?: number;
  maxLines?: number;
  className?: string;
  textClassName?: string;
  buttonClassName?: string;
  expandText?: string;
  collapseText?: string;
  initiallyExpanded?: boolean;
  avgCharsPerLine?: number;
}

const DEFAULT_TEXT_CLASS = "text-sm text-gray-700 leading-relaxed";
const DEFAULT_BUTTON_CLASS =
  "mt-2 inline-flex items-center text-xs font-medium transition-colors duration-200";

function htmlToPlainText(html: string): string {
  if (!html) return "";
  let h = html
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "");

  h = h.replace(/<br\s*\/?>/gi, "\n");

  const blockTags = [
    "address",
    "article",
    "aside",
    "blockquote",
    "canvas",
    "dd",
    "div",
    "dl",
    "dt",
    "fieldset",
    "figcaption",
    "figure",
    "footer",
    "form",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "header",
    "hr",
    "li",
    "main",
    "nav",
    "noscript",
    "ol",
    "output",
    "p",
    "pre",
    "section",
    "table",
    "tfoot",
    "ul",
    "video",
    "tr",
    "td",
    "th",
  ];
  const openBlockRe = new RegExp(
    `<\\s*(${blockTags.join("|")})(\\s+[^>]*)?>`,
    "gi"
  );
  const closeBlockRe = new RegExp(
    `<\\/\\s*(${blockTags.join("|")})\\s*>`,
    "gi"
  );
  h = h.replace(openBlockRe, "\n").replace(closeBlockRe, "\n");

  h = h.replace(/<\s*li(\s+[^>]*)?>/gi, "\n• ");

  let text = h.replace(/<[^>]+>/g, "");

  const entities: Record<string, string> = {
    "&nbsp;": " ",
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&quot;": '"',
    "&#39;": "'",
  };
  text = text.replace(
    /(&nbsp;|&amp;|&lt;|&gt;|&quot;|&#39;)/g,
    (m) => entities[m] ?? m
  );

  return text.replace(/\u00a0/g, " ").replace(/\r\n?/g, "\n");
}

function getTextLengthFromHtml(html: string): number {
  return htmlToPlainText(html).length;
}

function estimateLineCountFromHtml(html: string, avgCharsPerLine = 80): number {
  const text = htmlToPlainText(html);
  if (!text) return 0;

  const lines = text.split("\n");
  let estimated = 0;
  for (const line of lines) {
    const len = line.trimEnd().length;
    estimated += Math.max(1, Math.ceil(len / Math.max(1, avgCharsPerLine)));
  }
  return estimated;
}

const ExpandableDescription: React.FC<ExpandableDescriptionProps> = ({
  content,
  maxLength = 150,
  maxLines = 5,
  className = "",
  textClassName = DEFAULT_TEXT_CLASS,
  buttonClassName = DEFAULT_BUTTON_CLASS,
  expandText = "Read more",
  collapseText = "Read less",
  initiallyExpanded = false,
  avgCharsPerLine = 80,
}) => {
  const isPreview = useCardPreview();
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  const contentId = useId();

  const combinedContainerClassName = className
    ? `${className} w-full min-w-0`
    : "w-full min-w-0";
  const contentClassName = `${HTML_CONTENT_CLASS} ${textClassName} break-words`;

  const { needsExpand } = useMemo(() => {
    const length = getTextLengthFromHtml(content);
    const lineCount = estimateLineCountFromHtml(content, avgCharsPerLine);
    return {
      needsExpand: length > maxLength || lineCount > maxLines,
    };
  }, [content, maxLength, maxLines, avgCharsPerLine]);

  const safeContent = useMemo(() => sanitizeHtml(content), [content]);

  if (!content) return null;

  if (!needsExpand) {
    return (
      <div className={combinedContainerClassName}>
        <div
          id={contentId}
          className={contentClassName}
          dangerouslySetInnerHTML={{ __html: safeContent }}
        />
        <style
          dangerouslySetInnerHTML={{
            __html: getHtmlContentStyles(HTML_CONTENT_CLASS),
          }}
        />
      </div>
    );
  }

  const collapsedStyle: React.CSSProperties = {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: maxLines,
    overflow: "hidden",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  };

  return (
    <div className={combinedContainerClassName}>
      {isExpanded ? (
        <div
          id={contentId}
          className={contentClassName}
          dangerouslySetInnerHTML={{ __html: safeContent }}
        />
      ) : isPreview ? (
        <div
          id={contentId}
          className={contentClassName}
          style={collapsedStyle}
          dangerouslySetInnerHTML={{ __html: safeContent }}
        />
      ) : (
        <div
          id={contentId}
          className={`${contentClassName} cursor-pointer`}
          style={collapsedStyle}
          dangerouslySetInnerHTML={{ __html: safeContent }}
          role="button"
          tabIndex={0}
          onClick={() => setIsExpanded(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsExpanded(true);
            }
          }}
          aria-label="Click to expand description"
          aria-expanded={isExpanded}
          aria-controls={`${contentId}-expand-btn`}
        />
      )}

      <div dir="auto" className="text-start">
        <button
          id={`${contentId}-expand-btn`}
          type="button"
          onClick={isPreview ? undefined : () => setIsExpanded((v) => !v)}
          disabled={isPreview}
          tabIndex={isPreview ? -1 : undefined}
          aria-hidden={isPreview ? true : undefined}
          className={buttonClassName}
          aria-expanded={isExpanded}
          aria-controls={contentId}
        >
          <span className="me-1">{isExpanded ? collapseText : expandText}</span>
          {isExpanded ? (
            <HiChevronUp className="w-3 h-3" />
          ) : (
            <HiChevronDown className="w-3 h-3" />
          )}
        </button>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: getHtmlContentStyles(HTML_CONTENT_CLASS),
        }}
      />
    </div>
  );
};

export default ExpandableDescription;
